import { RenderQuality } from './types';

interface NavigatorCapabilityHints {
  deviceMemory?: number;
  hardwareConcurrency?: number;
}

export interface RenderProfile {
  quality: RenderQuality;
  targetFrameMs: number;
  maxCanvasPixels: number;
  maxDevicePixelRatio: number;
  particleLimit: number;
  shakeScale: number;
  animateTarget: boolean;
  useGlow: boolean;
}

const profiles: Record<RenderQuality, RenderProfile> = {
  full: {
    quality: 'full',
    targetFrameMs: 1000 / 60,
    maxCanvasPixels: 2_400_000,
    maxDevicePixelRatio: 2,
    particleLimit: 24,
    shakeScale: 1,
    animateTarget: true,
    useGlow: true,
  },
  efficient: {
    quality: 'efficient',
    targetFrameMs: 1000 / 30,
    maxCanvasPixels: 1_000_000,
    maxDevicePixelRatio: 1.5,
    particleLimit: 10,
    shakeScale: 0.35,
    animateTarget: false,
    useGlow: false,
  },
  reduced: {
    quality: 'reduced',
    targetFrameMs: 1000 / 24,
    maxCanvasPixels: 850_000,
    maxDevicePixelRatio: 1.25,
    particleLimit: 0,
    shakeScale: 0,
    animateTarget: false,
    useGlow: false,
  },
};

export const getRenderProfile = (quality: RenderQuality) => profiles[quality];

export const selectRenderQuality = (
  navigatorHints: NavigatorCapabilityHints,
  reducedMotion: boolean,
): RenderQuality => {
  if (reducedMotion) return 'reduced';

  const lowMemory = navigatorHints.deviceMemory !== undefined && navigatorHints.deviceMemory <= 4;
  const lowConcurrency =
    navigatorHints.hardwareConcurrency !== undefined && navigatorHints.hardwareConcurrency <= 4;

  return lowMemory || lowConcurrency ? 'efficient' : 'full';
};

export const getCanvasScale = (
  width: number,
  height: number,
  requestedDevicePixelRatio: number,
  profile: RenderProfile,
) => {
  const logicalPixels = Math.max(1, width * height);
  const pixelBudgetScale = Math.sqrt(profile.maxCanvasPixels / logicalPixels);
  return Math.max(
    0.1,
    Math.min(requestedDevicePixelRatio || 1, profile.maxDevicePixelRatio, pixelBudgetScale),
  );
};
